import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage, useAuth, api, JOB_CATEGORIES, COUNTRIES } from '@/lib/context';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { 
  Briefcase, Building2, Users, Clock, MapPin, Plus, Eye, 
  Loader2, ChevronRight, Settings, CreditCard, Globe, FileText,
  CheckCircle, Shield, Zap, Sparkles, CheckCircle2, Upload, ImagePlus, Car,
  Mail, Phone, Download, X, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { SubscriptionStatus } from '@/components/SubscriptionStatus';
import { WorkshopDashboard } from '@/components/WorkshopDashboard';
import { DurationCombobox } from '@/components/DurationCombobox';

// Company Dashboard Navigation
const CompanyNav = () => {
  const { t } = useLanguage();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <nav className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="text-2xl font-bold text-brand">KEIKKA</Link>
          
          <div className="hidden md:flex items-center gap-6">
            <Link to="/company/dashboard" className="text-muted-foreground hover:text-foreground transition">
              {t('nav.dashboard')}
            </Link>
            <Link to="/company/jobs" className="text-muted-foreground hover:text-foreground transition">
              {t('companyDashboard.myJobs')}
            </Link>
            <Link to="/company/profile" className="text-muted-foreground hover:text-foreground transition">
              {t('nav.profile')}
            </Link>
            <Link to="/jobs" className="text-muted-foreground hover:text-foreground transition">
              {t('nav.browse')}
            </Link>
          </div>

          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <span className="text-sm text-muted-foreground">{user?.nickname}</span>
            <Button variant="ghost" size="sm" onClick={() => { logout(); navigate('/'); }}>
              {t('auth.logout')}
            </Button>
          </div>
        </div>
      </div>
    </nav>
  );
};

// Company Dashboard
export const CompanyDashboard = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role !== 'company') {
      navigate('/');
      return;
    }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      const [jobsRes, profileRes] = await Promise.all([
        api.getMyJobs(),
        api.getCompanyProfile()
      ]);
      setJobs(jobsRes.data);
      setProfile(profileRes.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate trial days
  const trialDaysLeft = user?.trial_start 
    ? Math.max(0, 60 - Math.floor((Date.now() - new Date(user.trial_start).getTime()) / (1000 * 60 * 60 * 24)))
    : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <CompanyNav />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <Skeleton className="h-12 w-1/3 mb-8" />
          <div className="grid md:grid-cols-3 gap-6">
            {[1,2,3].map(i => <Skeleton key={i} className="h-48" />)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <CompanyNav />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="company-dashboard">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold">{t('companyDashboard.welcome', { name: user?.company_name })}</h1>
            <p className="text-muted-foreground">{t('companyDashboard.subtitle')}</p>
          </div>
          <Link to="/jobs/create">
            <Button data-testid="new-job-btn">
              <Plus className="mr-2 h-4 w-4" />
              {t('companyDashboard.newJob')}
            </Button>
          </Link>
        </div>

        {/* Trial/Subscription Banner with microcopy */}
        {user?.subscription_status === 'trial' && (
          <Card className="mb-8 border-brand bg-brand/5" data-testid="trial-banner">
            <CardContent className="py-4">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div className="flex items-center gap-4">
                  <Clock className="h-8 w-8 text-brand" />
                  <div>
                    <p className="font-semibold">{t('companyDashboard.trialBanner.title', { days: trialDaysLeft })}</p>
                    <p className="text-sm text-muted-foreground">{t('microcopy.company.trial.fullAccess')}</p>
                    <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <CheckCircle2 className="h-3 w-3 text-green-500" />
                        {t('microcopy.company.trial.riskFree')}
                      </span>
                      <span className="flex items-center gap-1">
                        <CheckCircle2 className="h-3 w-3 text-green-500" />
                        {t('microcopy.company.trial.cancelAnytime')}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <Link to="/company/subscription">
                    <Button variant="outline" data-testid="upgrade-btn">
                      <CreditCard className="mr-2 h-4 w-4" />
                      {t('payments.upgradeNow')}
                    </Button>
                  </Link>
                  <span className="text-xs text-muted-foreground">{t('microcopy.company.trial.noCardNeeded')}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* AI Matching Banner */}
        <Card className="mb-8 bg-gradient-to-r from-brand/5 to-brand/10 border-brand/20" data-testid="ai-banner">
          <CardContent className="py-4">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-brand/10 rounded-lg">
                <Sparkles className="h-5 w-5 text-brand" />
              </div>
              <div>
                <p className="font-medium">{t('microcopy.company.jobPost.aiMatchesTalent')}</p>
                <p className="text-sm text-muted-foreground">{t('microcopy.company.jobPost.noManualSearch')}</p>
                <div className="flex flex-wrap gap-3 mt-2 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3 text-green-500" />
                    {t('microcopy.company.jobPost.getApplicationsFast')}
                  </span>
                  <span className="flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3 text-green-500" />
                    {t('microcopy.company.jobPost.qualityOverQuantity')}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Grid */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <Card data-testid="stat-jobs">
            <CardHeader className="pb-2">
              <CardDescription>{t('companyDashboard.activeJobs')}</CardDescription>
              <CardTitle className="text-3xl">{jobs.filter(j => j.status === 'active').length}</CardTitle>
            </CardHeader>
          </Card>
          <Card data-testid="stat-applications">
            <CardHeader className="pb-2">
              <CardDescription>{t('companyDashboard.totalApplications')}</CardDescription>
              <CardTitle className="text-3xl">{jobs.reduce((sum, j) => sum + j.application_count, 0)}</CardTitle>
            </CardHeader>
          </Card>
          <Card data-testid="stat-profile">
            <CardHeader className="pb-2">
              <CardDescription>{t('companyDashboard.profileStatus')}</CardDescription>
              <CardTitle className="text-3xl">{user?.profile_completed ? t('workerDashboard.complete') : t('workerDashboard.incomplete')}</CardTitle>
            </CardHeader>
          </Card>
          <Card data-testid="stat-subscription">
            <CardHeader className="pb-2">
              <CardDescription>{t('companyDashboard.subscriptionStatus')}</CardDescription>
              <CardTitle className="text-3xl capitalize">{user?.subscription_status || t('common.none')}</CardTitle>
            </CardHeader>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Card className="card-hover">
            <CardHeader>
              <Briefcase className="h-8 w-8 text-brand mb-2" />
              <CardTitle>{t('companyDashboard.myJobs')}</CardTitle>
              <CardDescription>{t('companyDashboard.myJobsDesc')}</CardDescription>
            </CardHeader>
            <CardFooter>
              <Link to="/company/jobs" className="w-full">
                <Button variant="outline" className="w-full" data-testid="view-jobs-btn">
                  {t('companyDashboard.viewJobs')}
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </CardFooter>
          </Card>

          <Card className="card-hover">
            <CardHeader>
              <Building2 className="h-8 w-8 text-brand mb-2" />
              <CardTitle>{t('companyDashboard.companyProfile')}</CardTitle>
              <CardDescription>{t('companyDashboard.companyProfileDesc')}</CardDescription>
            </CardHeader>
            <CardFooter>
              <Link to="/company/profile" className="w-full">
                <Button variant="outline" className="w-full" data-testid="edit-profile-btn">
                  {t('companyDashboard.editProfile')}
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </CardFooter>
          </Card>

          <Card className="card-hover">
            <CardHeader>
              <FileText className="h-8 w-8 text-brand mb-2" />
              <CardTitle>{t('companyDashboard.serviceOffers')}</CardTitle>
              <CardDescription>{t('companyDashboard.serviceOffersDesc')}</CardDescription>
            </CardHeader>
            <CardFooter>
              <Link to="/company/profile" className="w-full">
                <Button variant="outline" className="w-full" data-testid="service-offers-btn">
                  {t('companyDashboard.manage')}
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </CardFooter>
          </Card>

          <Card className="card-hover">
            <CardHeader>
              <Car className="h-8 w-8 text-brand mb-2" />
              <CardTitle>{t('workshop.title')}</CardTitle>
              <CardDescription>{t('workshop.cardDesc')}</CardDescription>
            </CardHeader>
            <CardFooter>
              <Button 
                variant="outline" 
                className="w-full" 
                onClick={() => document.getElementById('workshop-section')?.scrollIntoView({ behavior: 'smooth' })}
                data-testid="workshop-quick-btn"
              >
                {t('companyDashboard.viewRequests')}
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </CardFooter>
          </Card>
        </div>

        {/* Subscription Status Section */}
        <div className="mt-8">
          <h2 className="text-xl font-semibold mb-4">{t('companyDashboard.subscriptionStatus')}</h2>
          <SubscriptionStatus onUpgrade={fetchData} />
        </div>

        {/* Recent Jobs */}
        {jobs.length > 0 && (
          <div className="mt-8">
            <h2 className="text-xl font-semibold mb-4">{t('companyDashboard.recentJobs')}</h2>
            <div className="space-y-4">
              {jobs.slice(0, 3).map((job) => (
                <Card key={job.id}>
                  <CardContent className="flex justify-between items-center py-4">
                    <div>
                      <p className="font-semibold">{job.title}</p>
                      <p className="text-sm text-muted-foreground">
                        {job.region}, {job.country} • {t('jobs.applicationsCount', { count: job.application_count })}
                      </p>
                    </div>
                    <Link to={`/company/jobs/${job.id}/applications`}>
                      <Button variant="outline" size="sm">
                        <Eye className="mr-2 h-4 w-4" />
                        {t('companyDashboard.viewApplications')}
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Workshop Dashboard Section */}
        <div id="workshop-section">
          <WorkshopDashboard />
        </div>
      </div>
    </div>
  );
};

// Create Job Page
export const CreateJobPage = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [showAiModal, setShowAiModal] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    duration: '',
    region: '',
    country: '',
    language_requirement: '',
    send_to_matching: false,
    allow_direct_contact: false
  });

  useEffect(() => {
    if (user?.role !== 'company') {
      navigate('/');
    }
  }, [user]);

  // AI Job Writer function
  const handleAiGenerate = async () => {
    if (!aiPrompt.trim()) {
      toast.error(t('microcopy.ai.enterDescription'));
      return;
    }
    
    setAiLoading(true);
    try {
      const response = await api.post('/ai/job-writer', {
        description: aiPrompt,
        language: user?.preferred_language
      });
      
      const aiResult = response.data;
      
      // Auto-fill the form with AI-generated content
      setFormData(prev => ({
        ...prev,
        title: aiResult.title || prev.title,
        description: aiResult.description || prev.description,
        category: aiResult.category || prev.category,
        duration: aiResult.duration || prev.duration,
        country: aiResult.location_country || prev.country,
        region: aiResult.location_city || prev.region,
        language_requirement: aiResult.language_required || prev.language_requirement
      }));
      
      setShowAiModal(false);
      setAiPrompt('');
      toast.success(t('microcopy.ai.jobGenerated'));
    } catch (error) {
      console.error('AI Job Writer error:', error);
      toast.error(error.response?.data?.detail || t('microcopy.ai.generationError'));
    } finally {
      setAiLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.createJob(formData);
      toast.success(t('companyDashboard.createJobPage.successMessage'));
      navigate('/company/jobs');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <CompanyNav />
      
      <div className="max-w-2xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8">{t('companyDashboard.createJobPage.title')}</h1>

        <Card>
          <CardContent className="pt-6">
            {/* AI Job Writer Button */}
            <div className="mb-6 p-4 bg-gradient-to-r from-brand/10 to-purple-500/10 rounded-lg border border-brand/20">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-brand/20 rounded-lg">
                    <Sparkles className="h-5 w-5 text-brand" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm">{t('microcopy.ai.jobWriter')}</h3>
                    <p className="text-xs text-muted-foreground">{t('microcopy.ai.jobWriterDesc')}</p>
                  </div>
                </div>
                <Dialog open={showAiModal} onOpenChange={setShowAiModal}>
                  <DialogTrigger asChild>
                    <Button variant="default" size="sm" className="gap-2" data-testid="ai-job-writer-btn">
                      <Sparkles className="h-4 w-4" />
                      {t('microcopy.ai.writeWithAi')}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <Sparkles className="h-5 w-5 text-brand" />
                        {t('microcopy.ai.jobWriterTitle')}
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <p className="text-sm text-muted-foreground">
                        {t('microcopy.ai.jobWriterInstructions')}
                      </p>
                      <div className="space-y-2">
                        <Label htmlFor="ai-prompt">{t('microcopy.ai.describeJob')}</Label>
                        <Textarea
                          id="ai-prompt"
                          placeholder={t('microcopy.ai.jobWriterPlaceholder')}
                          value={aiPrompt}
                          onChange={(e) => setAiPrompt(e.target.value)}
                          rows={3}
                          maxLength={200}
                          data-testid="ai-prompt-input"
                        />
                        <p className="text-xs text-muted-foreground text-right">{aiPrompt.length}/200</p>
                      </div>
                      <div className="bg-muted/50 rounded-lg p-3 space-y-1">
                        <p className="text-xs font-medium">{t('microcopy.ai.examplesTitle')}</p>
                        <p className="text-xs text-muted-foreground">• {'"Need 2 welders in Oslo for 3 months"'}</p>
                        <p className="text-xs text-muted-foreground">• {'"Looking for truck driver in Tallinn, Estonian speaking"'}</p>
                        <p className="text-xs text-muted-foreground">• {'"Part-time cleaner needed in Helsinki"'}</p>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowAiModal(false)}>
                        {t('common.cancel')}
                      </Button>
                      <Button onClick={handleAiGenerate} disabled={aiLoading || !aiPrompt.trim()} data-testid="ai-generate-btn">
                        {aiLoading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            {t('microcopy.ai.generating')}
                          </>
                        ) : (
                          <>
                            <Sparkles className="mr-2 h-4 w-4" />
                            {t('microcopy.ai.generate')}
                          </>
                        )}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="title">{t('companyDashboard.createJobPage.titleLabel')} *</Label>
                <Input
                  id="title"
                  placeholder={t('companyDashboard.createJobPage.titlePlaceholder')}
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  required
                  minLength={5}
                  data-testid="job-title-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">{t('companyDashboard.createJobPage.descriptionLabel')} *</Label>
                <Textarea
                  id="description"
                  placeholder={t('companyDashboard.createJobPage.descriptionPlaceholder')}
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  required
                  minLength={20}
                  rows={6}
                  data-testid="job-description-input"
                />
                <p className="text-xs text-muted-foreground">
                  {t('companyDashboard.createJobPage.contactHiddenNote')}
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('companyDashboard.createJobPage.categoryLabel')} *</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(v) => setFormData({...formData, category: v})}
                    required
                  >
                    <SelectTrigger data-testid="job-category-select">
                      <SelectValue placeholder={t('jobs.selectCategory')} />
                    </SelectTrigger>
                    <SelectContent>
                      {JOB_CATEGORIES.map((c) => (
                        <SelectItem key={c} value={c}>{t(`categories.${c}`)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>{t('companyDashboard.createJobPage.durationLabel')} *</Label>
                  <DurationCombobox
                    value={formData.duration}
                    onChange={(v) => setFormData({...formData, duration: v})}
                    placeholder={t('companyDashboard.createJobPage.durationPlaceholder')}
                    t={t}
                    data-testid="job-duration-select"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('companyDashboard.createJobPage.countryLabel')} *</Label>
                  <Select
                    value={formData.country}
                    onValueChange={(v) => setFormData({...formData, country: v})}
                    required
                  >
                    <SelectTrigger data-testid="job-country-select">
                      <SelectValue placeholder={t('auth.selectCountry')} />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map((c) => (
                        <SelectItem key={c.code} value={c.code}>{t(c.nameKey)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="region">{t('companyDashboard.createJobPage.regionLabel')} *</Label>
                  <Input
                    id="region"
                    placeholder={t('companyDashboard.createJobPage.regionPlaceholder')}
                    value={formData.region}
                    onChange={(e) => setFormData({...formData, region: e.target.value})}
                    required
                    data-testid="job-region-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="language">{t('companyDashboard.createJobPage.languageLabel')}</Label>
                <Input
                  id="language"
                  placeholder={t('companyDashboard.createJobPage.languagePlaceholder')}
                  value={formData.language_requirement}
                  onChange={(e) => setFormData({...formData, language_requirement: e.target.value})}
                  data-testid="job-language-input"
                />
              </div>

              <div className="space-y-4 pt-4 border-t">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>{t('companyDashboard.createJobPage.sendToMatching')}</Label>
                    <p className="text-sm text-muted-foreground">{t('companyDashboard.createJobPage.sendToMatchingDesc')}</p>
                  </div>
                  <Switch
                    checked={formData.send_to_matching}
                    onCheckedChange={(v) => setFormData({...formData, send_to_matching: v})}
                    data-testid="send-matching-switch"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label>{t('companyDashboard.createJobPage.allowDirectContact')}</Label>
                    <p className="text-sm text-muted-foreground">{t('companyDashboard.createJobPage.allowDirectContactDesc')}</p>
                  </div>
                  <Switch
                    checked={formData.allow_direct_contact}
                    onCheckedChange={(v) => setFormData({...formData, allow_direct_contact: v})}
                    data-testid="direct-contact-switch"
                  />
                </div>
              </div>

              <div className="flex gap-4 pt-4">
                <Button type="submit" className="flex-1" disabled={loading} data-testid="submit-job-btn">
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {t('companyDashboard.createJobPage.postJob')}
                </Button>
                <Button type="button" variant="outline" onClick={() => navigate('/company/jobs')}>
                  {t('common.cancel')}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Company Jobs List
export const CompanyJobsPage = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role !== 'company') {
      navigate('/');
      return;
    }
    fetchJobs();
  }, [user]);

  const fetchJobs = async () => {
    try {
      const res = await api.getMyJobs();
      setJobs(res.data);
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <CompanyNav />
      
      <div className="max-w-7xl mx-auto px-4 py-8" data-testid="company-jobs-page">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">{t('companyDashboard.myJobs')}</h1>
          <Link to="/jobs/create">
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              {t('companyDashboard.newJob')}
            </Button>
          </Link>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1,2,3].map(i => <Skeleton key={i} className="h-24" />)}
          </div>
        ) : jobs.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Briefcase className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-xl font-semibold mb-2">{t('companyDashboard.noJobsYet')}</p>
              <p className="text-muted-foreground mb-4">{t('companyDashboard.createFirstJob')}</p>
              <Link to="/jobs/create">
                <Button>{t('companyDashboard.newJob')}</Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {jobs.map((job) => (
              <Card key={job.id} className="card-hover" data-testid={`company-job-${job.id}`}>
                <CardContent className="flex justify-between items-center py-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-semibold">{job.title}</p>
                      <Badge variant={job.status === 'active' ? 'default' : 'secondary'}>
                        {job.status}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {job.region}, {job.country}
                      </span>
                      <span>{t(`categories.${job.category}`)}</span>
                      <span>{t(`durations.${job.duration}`)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-2xl font-bold">{job.application_count}</p>
                      <p className="text-xs text-muted-foreground">{t('jobs.applications')}</p>
                    </div>
                    <Link to={`/company/jobs/${job.id}/applications`}>
                      <Button variant="outline" data-testid={`view-applications-${job.id}`}>
                        <Eye className="mr-2 h-4 w-4" />
                        {t('common.viewDetails')}
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Job Applications Page
//
// Business rule: the company that owns the job listing views full
// candidate details for free. There is NO €2.99 unlock paywall on
// direct applications — those apply only to the separate marketplace
// contact-opening flow. Server-side authorisation is enforced by
// `get_job_applications` (checks `job.company_id == user.id`).
const APPLICATION_STATUSES = [
  { value: 'pending',     et: 'Uus',              en: 'New',           ru: 'Новая' },
  { value: 'viewed',      et: 'Läbivaatamisel',   en: 'Under review',  ru: 'На рассмотрении' },
  { value: 'contact',     et: 'Võta ühendust',    en: 'Reach out',     ru: 'Связаться' },
  { value: 'interview',   et: 'Vestlus',          en: 'Interview',     ru: 'Собеседование' },
  { value: 'accepted',    et: 'Palgatud',         en: 'Hired',         ru: 'Нанят' },
  { value: 'rejected',    et: 'Tagasi lükatud',   en: 'Rejected',      ru: 'Отклонена' },
];

const statusLabel = (statusValue, lang) => {
  const s = APPLICATION_STATUSES.find((x) => x.value === statusValue);
  if (!s) return statusValue;
  return s[lang] || s.en || s.value;
};

const statusColor = (statusValue) => ({
  pending: 'bg-orange-500 text-white',
  viewed: 'bg-slate-200 text-slate-800',
  contact: 'bg-blue-500 text-white',
  interview: 'bg-purple-500 text-white',
  accepted: 'bg-emerald-500 text-white',
  rejected: 'bg-slate-400 text-white',
}[statusValue] || 'bg-slate-200 text-slate-800');

export const JobApplicationsPage = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const jobId = window.location.pathname.split('/')[3];
  const [applications, setApplications] = useState([]);
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openAppId, setOpenAppId] = useState(null);
  const [cvPreviewUrl, setCvPreviewUrl] = useState(null);

  useEffect(() => {
    if (user?.role !== 'company') {
      navigate('/');
      return;
    }
    fetchData();
     
  }, [user, jobId]);

  const fetchData = async () => {
    try {
      const [jobRes, appsRes] = await Promise.all([
        api.getJob(jobId),
        api.getJobApplications(jobId)
      ]);
      setJob(jobRes.data);
      setApplications(appsRes.data);
    } catch (error) {
      toast.error(t('common.error'));
      navigate('/company/jobs');
    } finally {
      setLoading(false);
    }
  };

  // Open the candidate detail dialog. Marks the row as read server-side
  // (pending → viewed) and clears the "Uus" badge. No payment involved.
  const openCandidate = async (app) => {
    setOpenAppId(app.id);
    if (!app.read_at) {
      try {
        await api.markApplicationRead(app.id);
        setApplications((prev) => prev.map((a) => a.id === app.id
          ? { ...a, read_at: new Date().toISOString(), status: a.status === 'pending' ? 'viewed' : a.status }
          : a
        ));
      } catch (_) { /* non-blocking */ }
    }
  };

  const closeCandidate = () => {
    setOpenAppId(null);
    setCvPreviewUrl(null);
  };

  const setStatus = async (appId, status) => {
    try {
      await api.updateApplicationStatus(jobId, appId, status);
      setApplications((prev) => prev.map((a) => a.id === appId ? { ...a, status } : a));
      toast.success(t('companyDashboard.applicationsPage.statusUpdated') !== 'companyDashboard.applicationsPage.statusUpdated'
        ? t('companyDashboard.applicationsPage.statusUpdated')
        : 'Status uuendatud');
    } catch (err) {
      toast.error(t('common.error'));
    }
  };

  // Fetch the CV as a blob and hand the frontend a same-origin object
  // URL so it can render it inline in an <iframe> without triggering
  // the browser's download prompt.
  const previewCv = async (appId) => {
    try {
      const res = await api.downloadApplicantCv(appId, { inline: true });
      const url = URL.createObjectURL(res.data);
      setCvPreviewUrl(url);
    } catch (err) {
      toast.error(t('companyDashboard.applicationsPage.cvOpenError') !== 'companyDashboard.applicationsPage.cvOpenError'
        ? t('companyDashboard.applicationsPage.cvOpenError')
        : 'CV avamine ebaõnnestus');
    }
  };

  const openApp = applications.find((a) => a.id === openAppId) || null;

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <CompanyNav />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Skeleton className="h-12 w-1/2 mb-8" />
          <div className="space-y-4">
            {[1,2,3].map(i => <Skeleton key={i} className="h-32" />)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <CompanyNav />

      <div className="max-w-4xl mx-auto px-4 py-8" data-testid="job-applications-page">
        <div className="mb-8">
          <Link to="/company/jobs" className="text-sm text-muted-foreground hover:text-foreground mb-2 inline-block">
            {t('companyDashboard.applicationsPage.backToJobs')}
          </Link>
          <h1 className="text-3xl font-bold">{job?.title}</h1>
          <p className="text-muted-foreground">{t('jobs.applicationsCount', { count: applications.length })}</p>
        </div>

        {applications.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-xl font-semibold mb-2">{t('companyDashboard.applicationsPage.noApplications')}</p>
              <p className="text-muted-foreground">{t('companyDashboard.applicationsPage.shareToReceive')}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {applications.map((app) => {
              const isNew = !app.read_at && app.status === 'pending';
              const missingContact = !app.worker_email && !app.worker_phone;
              const missingCv = !app.cv_file_path && !app.cv_snapshot;
              return (
                <Card
                  key={app.id}
                  className={`transition ${isNew ? 'border-orange-300 bg-orange-50/40' : ''}`}
                  data-testid={`application-${app.id}`}
                >
                  <CardContent className="py-4 flex justify-between items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {isNew && (
                          <Badge className="bg-orange-500 text-white text-[10px] uppercase tracking-wider" data-testid={`app-new-badge-${app.id}`}>
                            {t('companyDashboard.applicationsPage.newBadge') !== 'companyDashboard.applicationsPage.newBadge'
                              ? t('companyDashboard.applicationsPage.newBadge')
                              : 'Uus'}
                          </Badge>
                        )}
                        <p className="font-semibold text-lg truncate">{app.worker_nickname}</p>
                        <Badge className={`text-[11px] ${statusColor(app.status)}`}>{statusLabel(app.status, language)}</Badge>
                      </div>
                      {app.cover_letter && (
                        <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{app.cover_letter}</p>
                      )}
                      <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
                        <span>{new Date(app.created_at).toLocaleDateString()}</span>
                        {app.worker_email && (
                          <span className="inline-flex items-center gap-1">
                            <Mail className="h-3 w-3" />{app.worker_email}
                          </span>
                        )}
                        {(missingContact || missingCv) && (
                          <span className="inline-flex items-center gap-1 text-amber-600" data-testid={`app-incomplete-${app.id}`}>
                            <AlertCircle className="h-3 w-3" />
                            {missingCv
                              ? (t('companyDashboard.applicationsPage.noCv') !== 'companyDashboard.applicationsPage.noCv'
                                  ? t('companyDashboard.applicationsPage.noCv')
                                  : 'CV puudub')
                              : (t('companyDashboard.applicationsPage.noContact') !== 'companyDashboard.applicationsPage.noContact'
                                  ? t('companyDashboard.applicationsPage.noContact')
                                  : 'Kontaktandmed puuduvad')}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="shrink-0">
                      <Button
                        onClick={() => openCandidate(app)}
                        data-testid={`view-candidate-${app.id}`}
                      >
                        <Eye className="mr-2 h-4 w-4" />
                        {t('companyDashboard.applicationsPage.viewCandidate') !== 'companyDashboard.applicationsPage.viewCandidate'
                          ? t('companyDashboard.applicationsPage.viewCandidate')
                          : 'Vaata kandidaati'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Candidate detail dialog — full free view for the job owner */}
      <Dialog open={!!openAppId} onOpenChange={(v) => { if (!v) closeCandidate(); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="candidate-detail-dialog">
          {openApp && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 flex-wrap">
                  {openApp.worker_nickname}
                  <Badge className={`text-[11px] ${statusColor(openApp.status)}`}>{statusLabel(openApp.status, language)}</Badge>
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-5">
                {/* Contact block */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
                    {openApp.worker_email ? (
                      <a href={`mailto:${openApp.worker_email}`} className="text-orange-600 hover:underline truncate" data-testid="candidate-email-link">
                        {openApp.worker_email}
                      </a>
                    ) : (
                      <span className="text-muted-foreground text-xs">{t('companyDashboard.applicationsPage.noContact') || 'e-post puudub'}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-muted-foreground shrink-0" />
                    {openApp.worker_phone ? (
                      <a href={`tel:${openApp.worker_phone}`} className="text-orange-600 hover:underline" data-testid="candidate-phone-link">
                        {openApp.worker_phone}
                      </a>
                    ) : (
                      <span className="text-muted-foreground text-xs">telefon puudub</span>
                    )}
                  </div>
                </div>

                {/* Status changer */}
                <div>
                  <Label className="text-xs">{t('companyDashboard.applicationsPage.status') || 'Status'}</Label>
                  <Select value={openApp.status} onValueChange={(v) => setStatus(openApp.id, v)}>
                    <SelectTrigger className="h-9 mt-1" data-testid="candidate-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {APPLICATION_STATUSES.map((s) => (
                        <SelectItem key={s.value} value={s.value} data-testid={`candidate-status-${s.value}`}>
                          {statusLabel(s.value, language)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Cover letter */}
                {openApp.cover_letter && (
                  <div>
                    <Label className="text-xs">{t('companyDashboard.applicationsPage.coverLetter') || 'Sõnum'}</Label>
                    <p className="mt-1 text-sm whitespace-pre-wrap bg-slate-50 p-3 rounded border">{openApp.cover_letter}</p>
                  </div>
                )}

                {/* CV block — inline preview + download */}
                {openApp.cv_file_path || openApp.cv_filename ? (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-xs">CV</Label>
                      <div className="flex gap-2">
                        {!cvPreviewUrl && (
                          <Button size="sm" variant="outline" onClick={() => previewCv(openApp.id)} data-testid="cv-preview-btn">
                            <FileText className="h-3.5 w-3.5 mr-1" /> {t('companyDashboard.applicationsPage.preview') || 'Vaata'}
                          </Button>
                        )}
                        <a
                          href={`${process.env.REACT_APP_BACKEND_URL}/api/applications/company/${openApp.id}/cv`}
                          target="_blank"
                          rel="noreferrer"
                          data-testid="cv-download-link"
                        >
                          <Button size="sm" variant="outline">
                            <Download className="h-3.5 w-3.5 mr-1" /> {t('companyDashboard.applicationsPage.download') || 'Lae alla'}
                          </Button>
                        </a>
                      </div>
                    </div>
                    {cvPreviewUrl && (
                      <div className="border rounded overflow-hidden" style={{ height: '60vh' }}>
                        <iframe src={cvPreviewUrl} className="w-full h-full" title="CV preview" data-testid="cv-preview-frame" />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-xs text-amber-600 inline-flex items-center gap-1">
                    <AlertCircle className="h-3.5 w-3.5" />
                    {t('companyDashboard.applicationsPage.noCv') || 'Kandidaat ei ole CV-d üles laadinud'}
                  </div>
                )}

                {/* Structured CV snapshot (for logged-in applicants) */}
                {openApp.cv_snapshot && (
                  <div className="space-y-2">
                    {openApp.cv_snapshot.title && (
                      <div><Label className="text-xs">Roll</Label><p className="text-sm">{openApp.cv_snapshot.title}</p></div>
                    )}
                    {Array.isArray(openApp.cv_snapshot.skills) && openApp.cv_snapshot.skills.length > 0 && (
                      <div><Label className="text-xs">Oskused</Label>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {openApp.cv_snapshot.skills.map((s, i) => <Badge key={i} variant="outline" className="text-[10px]">{typeof s === 'string' ? s : s.name}</Badge>)}
                        </div>
                      </div>
                    )}
                    {Array.isArray(openApp.cv_snapshot.languages) && openApp.cv_snapshot.languages.length > 0 && (
                      <div><Label className="text-xs">Keeled</Label>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {openApp.cv_snapshot.languages.map((l, i) => <Badge key={i} variant="outline" className="text-[10px]">{typeof l === 'string' ? l : `${l.code} · ${l.level || ''}`}</Badge>)}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Company Profile Page
export const CompanyProfilePage = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [profile, setProfile] = useState({
    services_categories: [],
    available_workers: {},
    regions_served: [],
    languages_supported: [],
    description: '',
    logo_url: null
  });
  const [newWorkerType, setNewWorkerType] = useState('');
  const [newWorkerCount, setNewWorkerCount] = useState('');
  const logoInputRef = React.useRef(null);

  useEffect(() => {
    if (user?.role !== 'company') {
      navigate('/');
      return;
    }
    fetchProfile();
  }, [user]);

  const fetchProfile = async () => {
    try {
      const res = await api.getCompanyProfile();
      setProfile(res.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(t('companyProfile.logoInvalidType'));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('companyProfile.logoTooLarge'));
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('logo', file);
      const res = await api.uploadCompanyLogo(formData);
      setProfile({ ...profile, logo_url: res.data.logo_url });
      toast.success(t('companyProfile.logoUploaded'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleDeleteLogo = async () => {
    try {
      await api.deleteCompanyLogo();
      setProfile({ ...profile, logo_url: null, logo_id: null });
      toast.success(t('companyProfile.logoDeleted'));
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateCompanyProfile(profile);
      toast.success(t('companyProfile.profileSaved'));
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const addCategory = (category) => {
    if (category && !profile.services_categories.includes(category)) {
      setProfile({...profile, services_categories: [...profile.services_categories, category]});
    }
  };

  const removeCategory = (category) => {
    setProfile({...profile, services_categories: profile.services_categories.filter(c => c !== category)});
  };

  const addWorker = () => {
    if (newWorkerType && newWorkerCount) {
      setProfile({
        ...profile,
        available_workers: {...profile.available_workers, [newWorkerType]: parseInt(newWorkerCount)}
      });
      setNewWorkerType('');
      setNewWorkerCount('');
    }
  };

  const removeWorker = (type) => {
    const workers = {...profile.available_workers};
    delete workers[type];
    setProfile({...profile, available_workers: workers});
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <CompanyNav />
        <div className="max-w-2xl mx-auto px-4 py-8">
          <Skeleton className="h-12 w-1/2 mb-8" />
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <CompanyNav />
      
      <div className="max-w-2xl mx-auto px-4 py-8" data-testid="company-profile-page">
        <h1 className="text-3xl font-bold mb-8">{t('companyProfile.title')}</h1>

        <Card>
          <CardContent className="pt-6 space-y-6">
            {/* Description */}
            <div className="space-y-2">
              <Label>{t('companyProfile.description')}</Label>
              <Textarea
                placeholder={t('companyProfile.descriptionPlaceholder')}
                value={profile.description}
                onChange={(e) => setProfile({...profile, description: e.target.value})}
                rows={4}
                data-testid="company-description-input"
              />
            </div>

            {/* Company Logo */}
            <div className="space-y-2">
              <Label>{t('companyProfile.logo')}</Label>
              <div className="flex items-center gap-4">
                {profile.logo_url ? (
                  <div className="relative">
                    <img 
                      src={`${process.env.REACT_APP_BACKEND_URL}${profile.logo_url}`}
                      alt="Company logo"
                      className="w-20 h-20 rounded-lg object-cover border"
                      data-testid="company-logo-preview"
                    />
                    <Button
                      variant="destructive"
                      size="icon"
                      className="absolute -top-2 -right-2 h-6 w-6"
                      onClick={handleDeleteLogo}
                      data-testid="delete-logo-btn"
                    >
                      ×
                    </Button>
                  </div>
                ) : (
                  <div className="w-20 h-20 rounded-lg border-2 border-dashed border-muted-foreground/25 flex items-center justify-center">
                    <Building2 className="h-8 w-8 text-muted-foreground/50" />
                  </div>
                )}
                <div className="flex-1">
                  <input
                    type="file"
                    ref={logoInputRef}
                    onChange={handleLogoUpload}
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    className="hidden"
                    data-testid="logo-file-input"
                  />
                  <Button
                    variant="outline"
                    onClick={() => logoInputRef.current?.click()}
                    disabled={uploadingLogo}
                    data-testid="upload-logo-btn"
                  >
                    {uploadingLogo ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t('common.uploading')}
                      </>
                    ) : (
                      <>
                        <Plus className="mr-2 h-4 w-4" />
                        {profile.logo_url ? (t('companyProfile.changeLogo')) : (t('companyProfile.uploadLogo'))}
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('companyProfile.logoHelper')}
                  </p>
                </div>
              </div>
            </div>

            {/* Service Categories */}
            <div className="space-y-2">
              <Label>{t('companyProfile.serviceCategories')}</Label>
              <Select onValueChange={addCategory}>
                <SelectTrigger data-testid="add-category-select">
                  <SelectValue placeholder={t('companyProfile.addCategory')} />
                </SelectTrigger>
                <SelectContent>
                  {JOB_CATEGORIES.filter(c => !profile.services_categories.includes(c)).map((c) => (
                    <SelectItem key={c} value={c}>{t(`categories.${c}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex flex-wrap gap-2 mt-2">
                {profile.services_categories.map((cat) => (
                  <Badge key={cat} variant="secondary" className="cursor-pointer" onClick={() => removeCategory(cat)}>
                    {t(`categories.${cat}`)} ×
                  </Badge>
                ))}
              </div>
            </div>

            {/* Available Workers */}
            <div className="space-y-2">
              <Label>{t('companyProfile.availableWorkers')} ({t('companyProfile.availableWorkersSubtitle')})</Label>
              <div className="flex gap-2">
                <Input
                  placeholder={t('companyProfile.workerType')}
                  value={newWorkerType}
                  onChange={(e) => setNewWorkerType(e.target.value)}
                  data-testid="worker-type-input"
                />
                <Input
                  type="number"
                  placeholder={t('companyProfile.count')}
                  value={newWorkerCount}
                  onChange={(e) => setNewWorkerCount(e.target.value)}
                  className="w-24"
                  data-testid="worker-count-input"
                />
                <Button type="button" onClick={addWorker} data-testid="add-worker-btn">{t('companyProfile.addWorker')}</Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {Object.entries(profile.available_workers).map(([type, count]) => (
                  <Badge key={type} variant="outline" className="cursor-pointer" onClick={() => removeWorker(type)}>
                    {count} {type} ×
                  </Badge>
                ))}
              </div>
            </div>

            {/* Regions Served */}
            <div className="space-y-2">
              <Label>{t('companyProfile.regionsServed')}</Label>
              <Input
                placeholder={t('companyProfile.regionsPlaceholder')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const val = e.target.value.trim();
                    if (val && !profile.regions_served.includes(val)) {
                      setProfile({...profile, regions_served: [...profile.regions_served, val]});
                      e.target.value = '';
                    }
                    e.preventDefault();
                  }
                }}
                data-testid="regions-input"
              />
              <div className="flex flex-wrap gap-2 mt-2">
                {profile.regions_served.map((region) => (
                  <Badge key={region} variant="secondary" className="cursor-pointer"
                    onClick={() => setProfile({...profile, regions_served: profile.regions_served.filter(r => r !== region)})}>
                    {region} ×
                  </Badge>
                ))}
              </div>
            </div>

            {/* Languages */}
            <div className="space-y-2">
              <Label>{t('companyProfile.languagesSupported')}</Label>
              <Input
                placeholder={t('companyProfile.languagesPlaceholder')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const val = e.target.value.trim();
                    if (val && !profile.languages_supported.includes(val)) {
                      setProfile({...profile, languages_supported: [...profile.languages_supported, val]});
                      e.target.value = '';
                    }
                    e.preventDefault();
                  }
                }}
                data-testid="languages-input"
              />
              <div className="flex flex-wrap gap-2 mt-2">
                {profile.languages_supported.map((lang) => (
                  <Badge key={lang} variant="secondary" className="cursor-pointer"
                    onClick={() => setProfile({...profile, languages_supported: profile.languages_supported.filter(l => l !== lang)})}>
                    {lang} ×
                  </Badge>
                ))}
              </div>
            </div>

            <Button onClick={handleSave} disabled={saving} className="w-full" data-testid="save-profile-btn">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('companyProfile.saveProfile')}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
